// lib
import { useContext, useState, useRef, useMemo, useCallback, useEffect } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'

import { DataContext } from '@aytm/app/stats/page/question-views/xq/Component'

import { encodeText, decodeText } from '@aytm/app/stats/lib/util/helpers'
import { pressable } from '@aytm/app/stats/components/lib/pressable'

// components
import { InputTextarea } from 'primereact/inputtextarea'
import { Dropdown } from 'primereact/dropdown'

// styles
import style from '@aytm/app/stats/page/question-views/xq/styles/ScriptItem.module.scss'

const MODE_TEXTS = {
  advanced: 'switch to basic mode',
  basic: 'switch to advanced mode',
  basicDisabled: 'simplify/clear logic to switch to the basic mode'
}
const TEXT_WIDTH = 448
const TEXT_VPADDING = 9
const TEXT_FONT_SIZE = 14
const TEXT_LINE_HEIGHT = 16
const TEXT_MIN_HEIGHT = 34
const TEXT_MAX_HEIGHT = 80

const onScriptItemValueKeyDown = event => 13 == event.keyCode && event.preventDefault()

const ScriptItemText = ({ item, value: storedValue, basic }) => {
  // a newer manifest can name an arg that a saved question never stored
  const value = storedValue ?? ''
  const { model, disabled, onChange } = useContext(DataContext)
  const titleId = `stat-xq-${model?.id}-arg-${item?.id}-title`

  const [height, setHeight] = useState(TEXT_MIN_HEIGHT)
  const [minHeight, setMinHeight] = useState(TEXT_MIN_HEIGHT)

  const [mode, setMode] = useState('advanced')

  const [error, setError] = useState(false)
  const [basicDisabled, setBasicDisabled] = useState(false)

  const editRef = useRef(false)
  const callbackRef = useRef(null)

  const basicOptions = useMemo(
    () =>
      basic && 0 < basic.length
        ? basic.map(r => {
            return { text: r.text, key: r.value }
          })
        : null,
    [basic]
  )

  const onBasicChange = useCallback(
    option => {
      // Compare by content, not identity — values may be arrays (distinct instances each render)
      if (JSON.stringify(value) === JSON.stringify(option)) return

      model.args[item.id] = option
      onChange(model)
    },
    [item.id, value, model, onChange]
  )

  const onScriptItemValueChange = useCallback(
    event => {
      const text = encodeText(event.target.value)
      if (value == text) return

      model.args[item.id] = text
      onChange(model)
    },
    [item.id, value, model, onChange]
  )

  const onScriptItemValuePaste = useCallback(
    event => {
      event.preventDefault()

      let text =
        (event.clipboardData || window.clipboardData).getData('Text') || event.clipboardData.getData('text/plain')
      text = text.replace(/(\r\n|\n|\r|\t|\v)/gm, ' ')

      const el = event.currentTarget

      const l = text.length
      const start = el.selectionStart
      const end = el.selectionEnd

      const valueBefore = el.value.slice(0, start)
      const valueAfter = el.value.slice(end)
      text = encodeText(`${valueBefore}${text}${valueAfter}`)

      if (value != text) {
        model.args[item.id] = text
        onChange(model)
      }

      callbackRef.current = () => {
        el.setSelectionRange(start + l, start + l)
        el.blur()
        el.focus()
      }
    },
    [item.id, value, model, onChange]
  )

  const onScriptItemValueFocus = () => (editRef.current = true)

  const onScriptItemValueBlur = useCallback(() => {
    editRef.current = true

    setError(item.required && !(Array.isArray(value) ? value.length : (value || '').trim().length))
  }, [item.required, value])

  useEffect(() => {
    if (null !== callbackRef.current && 'function' == typeof callbackRef.current) {
      callbackRef.current()
      callbackRef.current = null
    }
  }, [value])

  useEffect(() => {
    const basicDisabled = 0 < value.length && basic && 0 < basic.length && !basic.some(r => r.value == value)

    setMode(basicDisabled || !basic || !basic.length ? 'advanced' : mode)
    setBasicDisabled(basicDisabled)

    editRef.current && setError(item.required && !(Array.isArray(value) ? value.length : (value || '').trim().length))
  }, [item.required, value, basic, mode])

  useEffect(() => {
    const div = document.createElement('div')

    document.body.appendChild(div)

    div.style.position = 'absolute'
    div.style.left = '-1000px'
    div.style.top = '-1000px'
    div.style.width = `${TEXT_WIDTH}px`
    div.style.fontSize = `${TEXT_FONT_SIZE}px`
    div.style.lineHeight = `${TEXT_LINE_HEIGHT}px`

    div.style.overflow = 'hidden'
    div.style['overflow-wrap'] = 'break-word'

    div.innerHTML = value || '&nbsp;'
    setHeight(Math.min(div.clientHeight + 2 * TEXT_VPADDING, TEXT_MAX_HEIGHT))

    div.innerHTML = item.default || item.placeholder || '&nbsp;'
    setMinHeight(Math.min(div.clientHeight + 2 * TEXT_VPADDING, TEXT_MAX_HEIGHT))

    div.parentNode.removeChild(div)
  }, [item.default, item.placeholder, value])

  const renderAdvancedMode = () => (
    <InputTextarea
      className={classNames('p-inputtext-rounded', { 'p-invalid': error })}
      aria-labelledby={titleId}
      placeholder={item.placeholder || ''}
      value={decodeText(value)}
      rows={1}
      autoResize
      onChange={onScriptItemValueChange}
      onKeyDown={onScriptItemValueKeyDown}
      onPaste={onScriptItemValuePaste}
      onFocus={onScriptItemValueFocus}
      onBlur={onScriptItemValueBlur}
      style={{
        height,
        minHeight,
        maxHeight: TEXT_MAX_HEIGHT
      }}
    />
  )

  const renderBasicMode = () => (
    <Dropdown
      className={classNames('p-dropdown-rounded', { 'p-invalid': error })}
      aria-labelledby={titleId}
      placeholder="Select..."
      value={value}
      options={basicOptions}
      optionValue="key"
      optionLabel="text"
      fixedDropdownDir="bottom"
      onChange={event => onBasicChange(event.value)}
    />
  )

  return (
    <div data-testid={`stat-xq-arg-${item?.id}`} className={style.item}>
      {disabled ? (
        <div className="p-col-12" dangerouslySetInnerHTML={{ __html: value }} />
      ) : (
        <>
          <div
            id={titleId}
            className={`${style['item-title']} p-col-4`}
            dangerouslySetInnerHTML={{ __html: item.text || item.id }}
          />
          <div className={`${style['item-component']} p-fluid p-col-8`}>
            <span className={style['item-note']}>Type: {item.type}</span>
            {basic && 0 < basic.length ? (
              <span
                data-testid={`stat-xq-arg-mode-toggle-${item?.id}`}
                className={classNames(style['item-toggle'], {
                  [style['item-toggle-disabled']]: basicDisabled
                })}
                {...pressable(() => setMode('basic' == mode ? 'advanced' : 'basic'), { disabled: basicDisabled })}
              >
                {MODE_TEXTS[basicDisabled ? 'basicDisabled' : mode]}
              </span>
            ) : null}
            {'advanced' == mode ? renderAdvancedMode() : renderBasicMode()}
          </div>
        </>
      )}
    </div>
  )
}

ScriptItemText.propTypes = {
  item: PropTypes.object,
  value: PropTypes.any,
  basic: PropTypes.array
}

ScriptItemText.displayName = 'ScriptItemText'

export default ScriptItemText
